import type { Armor, EquipmentRecord, EquipmentSlot, EquippableItem, Spellbook, Weapon } from "../types/items.js";

// ── Type guards ───────────────────────────────────────────────────────────────

export function isSpellbook(item: EquippableItem): item is Spellbook {
  return item.category === "misc" && "isSpellbook" in item && item.isSpellbook === true;
}

export function isShield(item: EquippableItem): item is Armor {
  return item.category === "armor" && (item as Armor).baseAC !== undefined && (item as Armor).addDex === true;
}

function isTwoHanded(item: EquippableItem): item is Weapon {
  return (
    item.category === "weapon" &&
    (item as Weapon).properties.includes("two-handed")
  );
}

// ── Factory ───────────────────────────────────────────────────────────────────

/** Return an EquipmentRecord with every slot empty. */
export function emptyEquipment(): EquipmentRecord {
  return {
    head: null,
    torso: null,
    gloves: null,
    boots: null,
    necklace: null,
    ring1: null,
    ring2: null,
    mainHand: null,
    offHand: null,
  };
}

// ── Spellbook queries ─────────────────────────────────────────────────────────

/** True when a spellbook is equipped in either hand slot. */
export function isSpellbookEquipped(equipment: EquipmentRecord): boolean {
  return (
    (equipment.mainHand !== null && isSpellbook(equipment.mainHand)) ||
    (equipment.offHand !== null && isSpellbook(equipment.offHand))
  );
}

/**
 * True when the character can currently cast spells.
 *
 * Requires a spellbook in hand AND no blocking item:
 * - A two-handed weapon in mainHand occupies both hands — no room for a spellbook.
 * - A shield in offHand blocks spellbook access (90s blobber rule).
 *
 * Note: this query reflects the *current* equipment state. Swapping weapons
 * mid-combat costs an action (resolved by combat-physical / I03).
 */
export function canCast(equipment: EquipmentRecord): boolean {
  if (!isSpellbookEquipped(equipment)) return false;

  // Two-handed weapon uses both hands → no free hand for spellbook
  if (equipment.mainHand !== null && isTwoHanded(equipment.mainHand)) return false;

  // Shield in off-hand blocks spellbook access
  if (equipment.offHand !== null && isShield(equipment.offHand)) return false;

  return true;
}

// ── Equip ─────────────────────────────────────────────────────────────────────

/**
 * Equip `item` into `slot` on top of `current`.
 * Returns the updated record and any items physically displaced by the operation.
 *
 * Physical displacement rules:
 * - Equipping a two-handed weapon in mainHand displaces the offHand item (both
 *   hands are now occupied).
 * - Equipping any item in a slot that already holds something displaces that item.
 *
 * Semantic conflicts (spellbook + shield, spellbook + two-hander) are NOT
 * auto-resolved here — `canCast()` is the query. The UI (I03) surfaces the
 * conflict and lets the player choose what to unequip.
 */
export function equipItem(
  item: EquippableItem,
  slot: EquipmentSlot,
  current: EquipmentRecord,
): { record: EquipmentRecord; displaced: EquippableItem[] } {
  const displaced: EquippableItem[] = [];

  // Displace whatever is currently in the target slot
  const existing = current[slot];
  if (existing !== null) displaced.push(existing);

  let next: EquipmentRecord = { ...current, [slot]: item };

  // Two-handed weapon in mainHand also claims offHand
  if (slot === "mainHand" && isTwoHanded(item)) {
    const offHandItem = current.offHand;
    if (offHandItem !== null) displaced.push(offHandItem);
    next = { ...next, offHand: null };
  }

  return { record: next, displaced };
}

/** Remove the item from `slot`, returning the updated record. */
export function unequipSlot(
  slot: EquipmentSlot,
  current: EquipmentRecord,
): { record: EquipmentRecord; item: EquippableItem | null } {
  const item = current[slot];
  return { record: { ...current, [slot]: null }, item };
}
